import type { Object3D } from "three";
import { algebraicToSquare, parseFen } from "../game/board";
import { ChessEngine } from "../game/engine";
import type { Exercise } from "../game/exercises";
import { captureSquaresOf } from "../game/moves";
import { SceneManager } from "../render/scene";
import { PieceLayer } from "../render/pieceEntities";
import { MoveMarkers } from "../render/highlights";
import { TaverneuBubble } from "./taverneuBubble";
import { sound } from "../audio/sound";

/**
 * Exercices pratiques : meme squelette que `TutorialController` (vraie scene 3D,
 * bulle du Taverneu persistante, navigation Precedent/Suivant) mais interactif —
 * l'utilisateur doit lui-meme selectionner une piece et jouer un coup, valide
 * contre un vrai `ChessEngine` reinitialise sur le FEN de l'exercice courant.
 *
 * Un coup legal qui n'est pas la solution n'est jamais applique a l'etat du
 * moteur (juste un message d'echec, la selection est relachee) : la position
 * ne peut donc jamais partir en desynchronisation avec `engine.getState()`.
 */
export class ExerciseController {
  private scene: SceneManager;
  private pieceLayer: PieceLayer;
  private markers: MoveMarkers;
  private bubble: TaverneuBubble;
  private hud: HTMLDivElement;
  private canvas: HTMLCanvasElement;

  private index = 0;
  private engine!: ChessEngine;
  private selected: number | null = null;
  private solved = false;
  private hoveredSquare: number | null = null;
  private pointerDownPos: { x: number; y: number } | null = null;

  constructor(
    uiRoot: HTMLElement,
    private readonly viewport: HTMLElement,
    private readonly exercises: readonly Exercise[],
    private readonly onQuit: () => void,
  ) {
    this.scene = new SceneManager(viewport);
    this.scene.setBaseAzimuth("w");
    this.canvas = this.scene.renderer.domElement;
    this.pieceLayer = new PieceLayer(this.scene.piecesGroup, this.scene.tweens);
    this.markers = new MoveMarkers(this.scene.piecesGroup);
    this.bubble = new TaverneuBubble(uiRoot);

    this.hud = document.createElement("div");
    this.hud.className = "tutorial-hud";
    this.hud.innerHTML = `
      <div class="tutorial-step-counter"></div>
      <div class="corner-buttons tutorial-quit">
        <button class="btn-corner" id="exercise-quit-btn" type="button"><span class="ico">&times;</span><span class="lbl">Quitter</span></button>
      </div>
      <div class="action-bar tutorial-actions">
        <button class="btn btn-ghost" id="exercise-prev-btn" type="button">Precedent</button>
        <button class="btn btn-ghost" id="exercise-hint-btn" type="button">Indice</button>
        <button class="btn btn-primary" id="exercise-next-btn" type="button">Suivant</button>
      </div>
    `;
    uiRoot.appendChild(this.hud);

    this.hud.querySelector("#exercise-quit-btn")?.addEventListener("click", () => this.quit());
    this.hud.querySelector("#exercise-prev-btn")?.addEventListener("click", () => this.go(-1));
    this.hud.querySelector("#exercise-next-btn")?.addEventListener("click", () => this.go(1));
    this.hud.querySelector("#exercise-hint-btn")?.addEventListener("click", () => this.showHint());

    this.canvas.addEventListener("pointerdown", (e) => {
      this.pointerDownPos = { x: e.clientX, y: e.clientY };
    });
    this.canvas.addEventListener("pointerup", (e) => this.onPointerUp(e));
    this.canvas.addEventListener("pointermove", (e) => this.updateHover(e.clientX, e.clientY));
    this.canvas.addEventListener("pointerleave", () => this.setHover(null));

    this.loadExercise();
  }

  private currentExercise(): Exercise {
    return this.exercises[this.index]!;
  }

  private loadExercise(): void {
    const exercise = this.currentExercise();
    this.engine = new ChessEngine(parseFen(exercise.fen));
    this.selected = null;
    this.solved = false;
    this.markers.hideAll();
    this.setHover(null);
    this.pieceLayer.syncFromBoard(this.engine.getState().board);
    this.bubble.say({ text: exercise.prompt }, { persistent: true });

    const counter = this.hud.querySelector<HTMLDivElement>(".tutorial-step-counter")!;
    counter.textContent = `${exercise.category} — Exercice ${this.index + 1} / ${this.exercises.length}`;

    const prevBtn = this.hud.querySelector<HTMLButtonElement>("#exercise-prev-btn")!;
    prevBtn.disabled = this.index === 0;
    const nextBtn = this.hud.querySelector<HTMLButtonElement>("#exercise-next-btn")!;
    nextBtn.textContent = this.index === this.exercises.length - 1 ? "Termine" : "Suivant";
    const hintBtn = this.hud.querySelector<HTMLButtonElement>("#exercise-hint-btn")!;
    hintBtn.disabled = false;
  }

  private go(delta: number): void {
    const next = this.index + delta;
    if (next < 0) return;
    if (next >= this.exercises.length) {
      this.quit();
      return;
    }
    this.index = next;
    this.loadExercise();
  }

  private showHint(): void {
    if (this.solved) return;
    const solution = this.currentExercise().solutions[0]!;
    this.markers.showSelection(algebraicToSquare(solution.from));
    this.bubble.say({ text: "Indice : regarde bien cette piece, l'ami." }, { persistent: true });
  }

  private squareOfIntersected(object: Object3D): number | undefined {
    let node: Object3D | null = object;
    while (node) {
      if (typeof node.userData.square === "number") return node.userData.square;
      node = node.parent;
    }
    return undefined;
  }

  private setHover(square: number | null): void {
    if (square === this.hoveredSquare) return;
    this.hoveredSquare = square;
    this.markers.showHover(square);
    this.canvas.style.cursor = square !== null ? "pointer" : "";
  }

  private updateHover(clientX: number, clientY: number): void {
    if (this.solved) {
      this.setHover(null);
      return;
    }
    const intersects = this.scene.raycastFromPointer(clientX, clientY, [this.scene.piecesGroup]);
    const hit = intersects[0];
    const square = hit ? this.squareOfIntersected(hit.object) : undefined;
    if (square === undefined) {
      this.setHover(null);
      return;
    }
    const piece = this.engine.getState().board[square];
    this.setHover(piece && piece.color === this.engine.turn ? square : null);
  }

  private onPointerUp(e: PointerEvent): void {
    if (!this.pointerDownPos) return;
    const dx = e.clientX - this.pointerDownPos.x;
    const dy = e.clientY - this.pointerDownPos.y;
    this.pointerDownPos = null;
    if (Math.hypot(dx, dy) > 6) return; // orbite camera, pas un tap
    if (this.solved) return;
    const intersects = this.scene.raycastFromPointer(e.clientX, e.clientY, this.scene.board.squareMeshes);
    const hit = intersects[0];
    if (!hit) return;
    const square = hit.object.userData.square as number;
    this.onSquareClicked(square);
  }

  private selectSquare(square: number): void {
    this.selected = square;
    const moves = this.engine.legalMovesFrom(square);
    this.markers.showSelection(square);
    this.markers.showTargets(
      moves.map((m) => m.to),
      captureSquaresOf(moves),
    );
  }

  private clearSelection(): void {
    this.selected = null;
    this.markers.hideAll();
  }

  private onSquareClicked(square: number): void {
    const piece = this.engine.getState().board[square];

    if (this.selected === null) {
      if (piece && piece.color === this.engine.turn) this.selectSquare(square);
      return;
    }

    if (square === this.selected) {
      this.clearSelection();
      return;
    }

    const candidates = this.engine.legalMovesFrom(this.selected).filter((m) => m.to === square);
    if (candidates.length === 0) {
      if (piece && piece.color === this.engine.turn) {
        this.selectSquare(square);
      } else {
        this.clearSelection();
      }
      return;
    }

    const from = this.selected;
    this.clearSelection();

    const exercise = this.currentExercise();
    const matches = exercise.solutions.some(
      (sol) => algebraicToSquare(sol.from) === from && algebraicToSquare(sol.to) === square,
    );

    if (!matches) {
      this.bubble.say({ text: exercise.failText }, { persistent: true });
      return;
    }

    const chosen = candidates[0]!;
    const entry = this.engine.move(from, square, chosen.promotion);
    if (!entry) return;
    this.solved = true;
    void this.pieceLayer.animateMove(entry.move).then(() => {
      sound.pieceMove();
      this.bubble.say({ text: exercise.successText }, { persistent: true });
    });
  }

  quit(): void {
    this.bubble.destroy();
    this.hud.remove();
    this.scene.dispose();
    this.viewport.querySelectorAll("canvas").forEach((c) => c.remove());
    this.onQuit();
  }
}
